// =============================================================================
// BP-07-03: DiagnosisHubPage
// Diagnosis landing page with sections and resources
// =============================================================================

import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Users, FileText, Stethoscope } from 'lucide-react';
import { useDiagnosis, useDiagnosisHub, useRelatedDiagnoses, useDiagnosisSpecialists } from '@/features/diagnoses/hooks/useDiagnosis';
import {
  DiagnosisHero,
  DiagnosisNav,
  ResourceGrid,
  ResourceCard,
  RelatedResources,
  ResourceBreadcrumb,
} from '../components';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function DiagnosisHubPage() {
  const { slug } = useParams<{ slug: string }>();

  // Section refs for navigation
  const overviewRef = useRef<HTMLElement>(null);
  const resourcesRef = useRef<HTMLElement>(null);
  const specialistsRef = useRef<HTMLElement>(null);
  const communityRef = useRef<HTMLElement>(null);

  // Fetch diagnosis data
  const { diagnosis, resources, isLoading, error } = useDiagnosisHub(slug!);
  const { data: relatedDiagnoses } = useRelatedDiagnoses(diagnosis?.id);
  const { data: specialists } = useDiagnosisSpecialists(slug!);

  // Navigation sections
  const sections = [
    { id: 'overview', label: 'Overview', ref: overviewRef },
    { id: 'resources', label: 'Resources', ref: resourcesRef },
    { id: 'specialists', label: 'Specialists', ref: specialistsRef },
    { id: 'community', label: 'Community', ref: communityRef },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="h-80 bg-gradient-to-b from-sage/10 to-cream animate-pulse" />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-warm-gray/20 rounded w-64" />
            <div className="h-4 bg-warm-gray/20 rounded w-full max-w-2xl" />
            <div className="h-4 bg-warm-gray/20 rounded w-full max-w-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error / 404 state
  if (error || !diagnosis) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl font-semibold text-navy mb-4">
            Diagnosis Not Found
          </h1>
          <p className="text-warm-gray mb-6">
            The diagnosis you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/diagnoses"
            className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
          >
            Browse All Diagnoses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{diagnosis.seoTitle || diagnosis.name} | BrightPath</title>
        <meta
          name="description"
          content={diagnosis.seoDescription || diagnosis.description || ''}
        />
        {diagnosis.heroImageUrl && (
          <meta property="og:image" content={diagnosis.heroImageUrl} />
        )}
      </Helmet>

      <div className="min-h-screen bg-cream">
        {/* Hero */}
        <DiagnosisHero diagnosis={diagnosis} />

        {/* Navigation */}
        <DiagnosisNav
          sections={sections.map((s) => ({ id: s.id, label: s.label }))}
        />

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Overview Section */}
          <section ref={overviewRef} id="overview" className="mb-16 scroll-mt-32">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-navy mb-6">
              Understanding {diagnosis.name}
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Main content */}
              <div className="md:col-span-2 space-y-6">
                {diagnosis.overview && (
                  <div className="prose prose-navy max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: diagnosis.overview }} />
                  </div>
                )}

                {/* Causes */}
                {diagnosis.causes && (
                  <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                    <h3 className="font-display text-lg font-semibold text-navy mb-3">
                      Causes
                    </h3>
                    <div
                      className="prose prose-sm prose-navy max-w-none"
                      dangerouslySetInnerHTML={{ __html: diagnosis.causes }}
                    />
                  </div>
                )}

                {/* Symptoms */}
                {diagnosis.symptoms && (
                  <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                    <h3 className="font-display text-lg font-semibold text-navy mb-3">
                      Signs & Symptoms
                    </h3>
                    <div
                      className="prose prose-sm prose-navy max-w-none"
                      dangerouslySetInnerHTML={{ __html: diagnosis.symptoms }}
                    />
                  </div>
                )}

                {/* Treatment */}
                {diagnosis.treatmentOptions && (
                  <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                    <h3 className="font-display text-lg font-semibold text-navy mb-3">
                      Treatment Options
                    </h3>
                    <div
                      className="prose prose-sm prose-navy max-w-none"
                      dangerouslySetInnerHTML={{ __html: diagnosis.treatmentOptions }}
                    />
                  </div>
                )}

                {/* Prognosis */}
                {diagnosis.prognosis && (
                  <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                    <h3 className="font-display text-lg font-semibold text-navy mb-3">
                      Prognosis & Outlook
                    </h3>
                    <div
                      className="prose prose-sm prose-navy max-w-none"
                      dangerouslySetInnerHTML={{ __html: diagnosis.prognosis }}
                    />
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="p-6 bg-sage/5 rounded-xl border border-sage/10">
                  <h3 className="font-display text-lg font-semibold text-navy mb-4">
                    Quick Facts
                  </h3>
                  <dl className="space-y-3">
                    {diagnosis.resourceCount !== undefined && (
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-sage" />
                        <div>
                          <dt className="text-sm text-warm-gray">Resources</dt>
                          <dd className="font-semibold text-navy">
                            {diagnosis.resourceCount}
                          </dd>
                        </div>
                      </div>
                    )}
                    {diagnosis.specialistTypes && (
                      <div className="flex items-center gap-3">
                        <Stethoscope className="w-5 h-5 text-sage" />
                        <div>
                          <dt className="text-sm text-warm-gray">Specialists</dt>
                          <dd className="font-semibold text-navy">
                            {diagnosis.specialistTypes.length} types
                          </dd>
                        </div>
                      </div>
                    )}
                  </dl>
                </div>

                {/* ICD-10 Codes */}
                {diagnosis.icd10Codes && diagnosis.icd10Codes.length > 0 && (
                  <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                    <h3 className="font-display text-sm font-semibold text-navy mb-3 uppercase tracking-wide">
                      ICD-10 Codes
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {diagnosis.icd10Codes.map((code) => (
                        <span
                          key={code}
                          className="px-2 py-1 text-sm font-mono bg-cream text-navy rounded"
                        >
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Diagnoses */}
                {relatedDiagnoses && relatedDiagnoses.length > 0 && (
                  <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                    <h3 className="font-display text-sm font-semibold text-navy mb-3 uppercase tracking-wide">
                      Related Conditions
                    </h3>
                    <ul className="space-y-2">
                      {relatedDiagnoses.map((related) => (
                        <li key={related.id}>
                          <Link
                            to={`/diagnoses/${related.slug}`}
                            className="flex items-center gap-2 text-warm-gray hover:text-sage transition-colors"
                          >
                            <ArrowRight className="w-4 h-4" />
                            <span>{related.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section ref={resourcesRef} id="resources" className="mb-16 scroll-mt-32">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-navy">
                Resources for {diagnosis.shortName || diagnosis.name}
              </h2>
              <Link
                to={`/resources?diagnosis=${diagnosis.slug}`}
                className="flex items-center gap-2 text-sage hover:text-sage-dark font-medium"
              >
                <span>View all</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {resources && resources.length > 0 ? (
              <ResourceGrid
                resources={resources.slice(0, 8)}
                columns={{ default: 1, sm: 2, lg: 4 }}
              />
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-warm-gray/10">
                <FileText className="w-12 h-12 text-warm-gray/30 mx-auto mb-4" />
                <p className="text-warm-gray">
                  No resources available for this diagnosis yet.
                </p>
                <Link
                  to="/resources"
                  className="inline-block mt-4 text-sage hover:text-sage-dark font-medium"
                >
                  Browse all resources
                </Link>
              </div>
            )}
          </section>

          {/* Specialists Section */}
          <section ref={specialistsRef} id="specialists" className="mb-16 scroll-mt-32">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-navy mb-6">
              Recommended Specialists
            </h2>

            {diagnosis.specialistTypes && diagnosis.specialistTypes.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {diagnosis.specialistTypes.map((type) => (
                  <div
                    key={type}
                    className="p-6 bg-white rounded-xl border border-warm-gray/10 hover:shadow-soft transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-lg bg-sage/10 flex items-center justify-center mb-4">
                      <Stethoscope className="w-6 h-6 text-sage" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-navy mb-2">
                      {type}
                    </h3>
                    <p className="text-sm text-warm-gray mb-4">
                      Find qualified {type.toLowerCase()} specialists experienced with{' '}
                      {diagnosis.shortName || diagnosis.name}.
                    </p>
                    <Link
                      to={`/marketplace?specialty=${encodeURIComponent(type)}`}
                      className="text-sm text-sage hover:text-sage-dark font-medium"
                    >
                      Find providers →
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-warm-gray/10">
                <Stethoscope className="w-12 h-12 text-warm-gray/30 mx-auto mb-4" />
                <p className="text-warm-gray">
                  Specialist recommendations coming soon.
                </p>
              </div>
            )}
          </section>

          {/* Community Section */}
          <section ref={communityRef} id="community" className="scroll-mt-32">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-navy mb-6">
              Connect with Others
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Support Groups */}
              <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                <div className="w-12 h-12 rounded-lg bg-community/10 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-community" />
                </div>
                <h3 className="font-display text-xl font-semibold text-navy mb-2">
                  Support Groups
                </h3>
                <p className="text-warm-gray mb-4">
                  Join families navigating {diagnosis.shortName || diagnosis.name}.
                  Share experiences, ask questions, and find support.
                </p>
                <Link
                  to={`/community/groups?diagnosis=${diagnosis.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-community/10 text-community rounded-lg hover:bg-community/20 transition-colors"
                >
                  <Users className="w-4 h-4" />
                  <span className="font-medium">Find Groups</span>
                </Link>
              </div>

              {/* Forums */}
              <div className="p-6 bg-white rounded-xl border border-warm-gray/10">
                <div className="w-12 h-12 rounded-lg bg-terracotta/10 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-terracotta" />
                </div>
                <h3 className="font-display text-xl font-semibold text-navy mb-2">
                  Discussion Forums
                </h3>
                <p className="text-warm-gray mb-4">
                  Browse discussions, get answers from experienced families,
                  and share your journey.
                </p>
                <Link
                  to={`/community/forums?diagnosis=${diagnosis.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta/10 text-terracotta rounded-lg hover:bg-terracotta/20 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  <span className="font-medium">Browse Forums</span>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default DiagnosisHubPage;

